<template>
  <slot
    :communities="communitiesForChannelResult?.communitiesForChannel ?? []"
    :selected-communities-ids="followedCommunitiesIds"
    :is-submitting="isSubmitting"
    @submit="submit"
  ></slot>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  CommunitiesForChannelDocument,
  FollowCommunitiesDocument,
  UserInChannelDocument,
} from "@/generated/graphql";
import { useUserStore } from "@/stores/user.store";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { useChannelStore } from "@/stores/channel.store";
import { useAwaitAction } from "@/composables/use-await-action";
import { useToastStore } from "@/stores/toast.store";
import { useI18n } from "vue-i18n";

const emit = defineEmits<{
  finish: [];
}>();

const userStore = useUserStore();
const toastStore = useToastStore();
const channelStore = useChannelStore();
const { t } = useI18n();

const { mutate: followCommunities } = useMutation(FollowCommunitiesDocument);

const { result: userInChannelResult } = useQuery(
  UserInChannelDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
  })),
  {
    enabled: computed(() => userStore.isAuthenticated),
  },
);

const { result: communitiesForChannelResult } = useQuery(
  CommunitiesForChannelDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
  })),
  {
    enabled: computed(() => channelStore.channelSlug !== null),
  },
);

const followedCommunitiesIds = computed(() => {
  const ids: string[] = [];
  const communities = userInChannelResult.value?.userInChannel?.communities;
  if (!communities) return ids;
  for (const community of communities) {
    ids.push(community.id);
  }

  return ids;
});

const { loading: isSubmitting, handler: submit } = useAwaitAction(
  async (
    communitiesToFollowIds: string[],
    communitiesToUnfollowIds: string[],
  ) => {
    const result = await followCommunities({
      communities_to_follow: communitiesToFollowIds,
      communities_to_unfollow: communitiesToUnfollowIds,
    });

    const response = result?.data?.followCommunities;
    if (response !== true) {
      toastStore.addToast({
        message: t("ui.communities.failedToFollowCommunities", {
          error: result?.errors?.toString() ?? "No response",
        }),
        type: "error",
      });
      return;
    }

    emit("finish");
  },
);
</script>
